import {
	DatePicker,
	DateRangePicker,
	DateRangePickerProps,
	FormField,
	Input,
	Multiselect,
	RadioGroup,
	Select,
	SpaceBetween,
	Textarea,
	TimeInput,
} from '@amzn/awsui-components-console';
import * as React from 'react';
import MultiInputHtmlElement from '../multiInputHtmlElement/multiInputHtmlElement';
import {
	convertStringToArray,
	convertStringListToOptions,
} from './taskInputsUtils';
import { TaskInputsProps } from './taskInputs.interface';
import { DataType } from '@amzn/snow-nxt-workflow-service-typescript-client';
import {
	isAttributeMandatory,
	isDerivedAttribute,
	isEmptyAttributeValue,
	isGlobalAttribute,
	isTaskSpecificAttribute,
} from 'src/utils/attributeTypeUtils';
import './taskInputs.scss';

export default function TaskInputs({
	taskInputs,
	setTaskInputs,
	isFirstSubmissionDone,
	isSubWorkflowTask = false,
}: TaskInputsProps) {
	const onFormInputChange = (value, index) => {
		const updatedTaskInputs = [...taskInputs];

		updatedTaskInputs.splice(index, 1, {
			...updatedTaskInputs[index],
			value: String(value),
		});
		setTaskInputs(updatedTaskInputs);
	};

	const getSelectedOptions = (value, index) => {
		const elements = value.split(',').map((item) => item.trim());

		return elements.filter((_, tokenIndex) => tokenIndex !== index).join(',');
	};

	const onFormObjectInputChange = (value, index) => {
		const updatedTaskInputs = [...taskInputs];

		updatedTaskInputs.splice(index, 1, {
			...updatedTaskInputs[index],
			value,
		});
		setTaskInputs(updatedTaskInputs);
	};

	return (
		<div>
			{taskInputs?.length > 0 && (
				<>
					<SpaceBetween size="xs">
						{taskInputs.map(
							(
								{
									attributeId,
									dataType,
									attributeName,
									description,
									helpText,
									value,
									attributeType,
									options,
									mandatory,
								},
								index
							) => {
								const shouldShow =
									isSubWorkflowTask ||
									isGlobalAttribute({ attributeType }) ||
									isDerivedAttribute({ attributeType });

								if (!shouldShow) return null;

								const label =
									isSubWorkflowTask &&
									isTaskSpecificAttribute({ attributeType })
										? attributeName?.slice(attributeName.indexOf('.') + 1)
										: attributeName;

								// Regex validation on task inputs is intentionally disabled for now;
								// only mandatory inputs get the required-field check.
								const errorText =
									isFirstSubmissionDone &&
									isAttributeMandatory({ mandatory }) &&
									isEmptyAttributeValue(value) &&
									!isSubWorkflowTask
										? 'This is a required field'
										: undefined;

								return (
									<React.Fragment key={attributeId}>
										<FormField
											key={attributeId}
											description={description}
											data-testid={`${attributeName}--${attributeId}`}
											constraintText={
												helpText ? <em>{helpText}</em> : undefined
											}
											errorText={errorText}
											label={label}
										>
											{dataType === DataType.TIME && (
												<TimeInput
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
													placeholder="hh:mm:ss"
												/>
											)}

											{dataType === DataType.DATE && (
												<DatePicker
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
													placeholder="YYYY/MM/DD"
												/>
											)}

											{dataType === DataType.DATE_TIME && (
												<div className="dateTimePicker">
													<DatePicker
														onChange={({ detail }) => {
															const timePart = (value as string)?.includes('T')
																? (value as string).split('T')[1]
																: '00:00:00';
															const concatValue = detail.value + 'T' + timePart;

															return onFormInputChange(concatValue, index);
														}}
														value={(() => {
															return (value as string)?.includes('T')
																? (value as string).split('T')[0]
																: '';
														})()}
														placeholder="YYYY/MM/DD"
													/>

													<TimeInput
														onChange={({ detail }) => {
															const datePart = (value as string)?.includes('T')
																? (value as string).split('T')[0]
																: '';

															return onFormInputChange(
																`${datePart}T${detail.value}.000Z`,
																index
															);
														}}
														value={(() => {
															let timePart = '00:00:00';

															if (
																(value as string)?.includes('T') &&
																(value as string)?.includes('.')
															) {
																timePart = (value as string)
																	.split('T')[1]
																	.split('.')[0];
															}

															return timePart;
														})()}
														placeholder="hh:mm:ss"
													/>
												</div>
											)}
											{dataType === DataType.DATE_RANGE && (
												<DateRangePicker
													onChange={({ detail }) => {
														return onFormObjectInputChange(detail.value, index);
													}}
													value={(() => {
														try {
															const parsedValue = JSON.parse(value as string);

															return {
																startDate: parsedValue?.startDate || null,
																endDate: parsedValue?.endDate || null,
																type: 'absolute',
															} as DateRangePickerProps.Value;
														} catch (error) {
															return {
																startDate: '',
																endDate: '',
																type: 'absolute',
															} as DateRangePickerProps.Value;
														}
													})()}
													isValidRange={(range) => {
														if (range?.type === 'absolute') {
															const [startDateWithoutTime] =
																range.startDate.split('T');
															const [endDateWithoutTime] =
																range.endDate.split('T');

															if (
																!startDateWithoutTime ||
																!endDateWithoutTime
															) {
																return {
																	valid: false,
																	errorMessage:
																		'The selected date range is incomplete. Select a start and end date for the date range.',
																};
															}

															if (
																new Date(range.startDate) >
																new Date(range.endDate)
															) {
																return {
																	valid: false,
																	errorMessage:
																		'The selected date range is invalid. The start date must be before the end date.',
																};
															}
														}

														return { valid: true };
													}}
													i18nStrings={{
														applyButtonLabel: 'Apply',
														cancelButtonLabel: 'Cancel',
														clearButtonLabel: 'Clear and Dismiss',
													}}
													placeholder="Select the date and time range"
													rangeSelectorMode="absolute-only"
													relativeOptions={[]}
												/>
											)}

											{dataType === DataType.NUMBER && (
												<Input
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
													type="number"
													inputMode="decimal"
												/>
											)}
											{dataType === DataType.STRING && !options && (
												<Input
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
													type="text"
												/>
											)}
											{dataType === DataType.BOOLEAN && (
												<RadioGroup
													value={String(value)}
													items={[
														{
															label: 'Yes',
															value: 'true',
														},
														{
															label: 'No',
															value: 'false',
														},
													]}
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
												/>
											)}
											{dataType === DataType.STRING && options && (
												<Select
													selectedOption={{
														label: value as string,
														value: value as string,
													}}
													onChange={({ detail }) =>
														onFormInputChange(
															detail.selectedOption.value,
															index
														)
													}
													options={convertStringListToOptions(options)}
												/>
											)}
											{dataType === DataType.PARAGRAPH && (
												<Textarea
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
												/>
											)}

											{dataType === DataType.JSON && (
												<Textarea
													onChange={({ detail }) =>
														onFormInputChange(detail.value, index)
													}
													value={value as string}
													rows={6}
												/>
											)}

											{dataType === DataType.LIST && options && (
												<Multiselect
													selectedOptions={convertStringToArray(
														value as string
													).map((listItem: string) => ({
														label: listItem,
														value: listItem,
													}))}
													onChange={({ detail }) => {
														const newSelectedOptions =
															detail.selectedOptions.map(({ value }) => value);

														onFormInputChange(
															newSelectedOptions.toString(),
															index
														);
													}}
													options={convertStringListToOptions(options)}
													placeholder="Choose options"
												/>
											)}
											{dataType === DataType.LIST && !options && (
												<MultiInputHtmlElement
													tokens={convertStringToArray(value as string).map(
														(listItem: string) => ({ label: listItem })
													)}
													onAdd={(input) => {
														onFormInputChange(
															String([
																...convertStringToArray(value as string),
																input,
															]),
															index
														);
													}}
													onRemove={(event) =>
														onFormInputChange(
															getSelectedOptions(value, event.detail.itemIndex),
															index
														)
													}
												/>
											)}
										</FormField>
									</React.Fragment>
								);
							}
						)}
					</SpaceBetween>
				</>
			)}
			{(!taskInputs || taskInputs.length === 0) && (
				<>There are no input fields in this workflow.</>
			)}
		</div>
	);
}
